if (!customElements.get('delivery-time')) {
  customElements.define(
    'delivery-time',
    class DeliveryTime extends HTMLElement {
      connectedCallback() {
        this.section = document.getElementById(this.dataset.sectionId);
        if (!this.section) return;

        this.block = this.querySelector('[data-delivery-time]') || this;

        this.onVariantChange = (event) => {
          const detail = event.detail || {};
          const variantId = detail?.variant?.id || detail?.variantId || detail?.id || null;
          this.refreshWithFade(variantId);
        };

        document.addEventListener('variant:change', this.onVariantChange);
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this.onVariantChange);
        if (this.abortController) this.abortController.abort();
      }

      async refreshWithFade(variantId) {
        // Cancel any in-flight request so older responses can't overwrite newer
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        // Start fade-out
        const block = this.block || this;
        block.classList.add('fade-out');

        // Wait for fade-out to finish (or fallback after ~350ms)
        await this.waitForFadeOut(block, 350);

        // Fetch updated section HTML rendered for the *new* variant
        const newEl = await this.fetchUpdatedElement(variantId);
        if (!newEl) {
          // Still fade back in even if nothing fetched
          block.classList.remove('fade-out');
          return;
        }

        // Update DOM
        this.updateBlock(newEl);

        // Fade back in
        // Small rAF helps ensure the browser notices the DOM change before removing class
        requestAnimationFrame(() => block.classList.remove('fade-out'));
      }

      waitForFadeOut(el, fallbackMs) {
        return new Promise((resolve) => {
          let done = false;

          const finish = () => {
            if (done) return;
            done = true;
            el.removeEventListener('transitionend', onEnd);
            clearTimeout(timer);
            resolve();
          };

          const onEnd = (e) => {
            // Only resolve for opacity transition on this element
            if (e.target === el && e.propertyName === 'opacity') finish();
          };

          el.addEventListener('transitionend', onEnd, { once: false });

          const timer = setTimeout(finish, fallbackMs);
        });
      }

      async fetchUpdatedElement(variantId) {
        const sectionIdRaw = this.dataset.sectionId.replace('shopify-section-', '');

        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionIdRaw);
        if (variantId) url.searchParams.set('variant', String(variantId));

        let htmlText;
        try {
          const res = await fetch(url.toString(), {
            signal: this.abortController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          htmlText = await res.text();
        } catch (err) {
          if (err?.name !== 'AbortError') console.error(err);
          return null;
        }

        const doc = new DOMParser().parseFromString(htmlText, 'text/html');

        return (
          doc.querySelector(`delivery-time[data-section-id="${this.dataset.sectionId}"]`) ||
          doc.querySelector('delivery-time')
        );
      }

      updateBlock(element) {
        if (!element) {
          this.replaceChildren();
          return;
        }

        if (element.innerHTML === this.innerHTML) return;

        const fragment = document.createDocumentFragment();
        Array.from(element.childNodes).forEach((node) => {
          fragment.appendChild(node.cloneNode(true));
        });

        this.replaceChildren(fragment);

        // Re-find the block after replacing children
        this.block = this.querySelector('[data-delivery-time]') || this;
      }
    }
  );
}
